import { useEffect, useRef } from 'react';
import type { User } from 'oidc-client-ts';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { isSignoutInteractionState } from '@/app/auth/interactiveLogout';
import { useUserManager } from '@/app/providers';
import { AuthenticationPageLayout } from '@/app/shell/AuthenticationPageLayout';
import { NgButtonNavLink } from '@/ui/buttons/NgButtonNavLink';

type AuthenticationAction = 'login' | 'login-callback' | 'login-failed' | 'logout' | 'profile' | 'register';

export function AuthenticationPage({ action }: { action: AuthenticationAction }) {
  return <AuthenticationPageLayout>{renderAction(action)}</AuthenticationPageLayout>;
}

function renderAction(action: AuthenticationAction) {
  switch (action) {
    case 'login':
      return <LoginAction />;
    case 'login-callback':
      return <LoginCallbackAction />;
    case 'login-failed':
      return <LoginFailed />;
    case 'logout':
      return <LogoutAction />;
    case 'profile':
      return <p>Editing the profile is not supported.</p>;
    case 'register':
      return <p>Registration is not supported.</p>;
  }
}

type ReturnUrlState = { returnUrl?: string } | null;
type MessageState = { message?: string } | null;

function toAppRelativeUrl(returnUrl: unknown): string {
  if (typeof returnUrl !== 'string' || returnUrl === '') {
    return '/';
  }

  if (returnUrl.startsWith(`${window.location.origin}/`) || returnUrl === window.location.origin) {
    return returnUrl.slice(window.location.origin.length) || '/';
  }

  const resolvesToAnotherOrigin = returnUrl.startsWith('//') || returnUrl.startsWith('/\\');
  if (returnUrl.startsWith('/') && !resolvesToAnotherOrigin) {
    return returnUrl;
  }

  return '/';
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function LoginAction() {
  const userManager = useUserManager();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const startedRef = useRef(false);

  const returnUrl =
    (location.state as ReturnUrlState)?.returnUrl ??
    searchParams.get('returnUrl') ??
    `${window.location.origin}/`;

  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;

    void userManager.signinRedirect({ state: { returnUrl } });
  }, [userManager, returnUrl]);

  return (
    <p className="fade-in-delay">
      {t('Authentication_LoggingIn_Label', { defaultValue: 'Logging user in' })}
    </p>
  );
}

let signinCallbackPromiseSharedAcrossStrictModeDoubledEffects: Promise<User> | null = null;

function LoginCallbackAction() {
  const userManager = useUserManager();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    signinCallbackPromiseSharedAcrossStrictModeDoubledEffects ??= userManager.signinRedirectCallback();
    signinCallbackPromiseSharedAcrossStrictModeDoubledEffects
      .then((user) => {
        const returnUrl = (user.state as ReturnUrlState)?.returnUrl;
        void navigate(toAppRelativeUrl(returnUrl), { replace: true });
      })
      .catch((error: unknown) => {
        void navigate('/authentication/login-failed', {
          replace: true,
          state: { message: errorMessage(error) },
        });
      });
  }, [userManager, navigate]);

  return (
    <p className="fade-in-delay">
      {t('Authentication_CompletingLoggingIn_Label', { defaultValue: 'Logging user in' })}
    </p>
  );
}

function LoginFailed() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const message = (location.state as MessageState)?.message ?? searchParams.get('message');

  const label = t('Authentication_LogInFailed_Label', { defaultValue: 'Login failed' });
  const fullMessage = label + (message === null || message === undefined || message.trim() === '' ? '' : ` – ${message}`);

  return (
    <div>
      <p>{fullMessage}</p>
      <p>
        <NgButtonNavLink
          to="/incident-list"
          text={t('Authentication_LogInFailed_TryAgain_Button', { defaultValue: 'Try again' })}
        />
      </p>
    </div>
  );
}

function LogoutAction() {
  const userManager = useUserManager();
  const location = useLocation();
  const startedRef = useRef(false);

  const initiatedInPage = isSignoutInteractionState(location.state);

  useEffect(() => {
    if (!initiatedInPage || startedRef.current) {
      return;
    }
    startedRef.current = true;

    void userManager.signoutRedirect();
  }, [initiatedInPage, userManager]);

  if (!initiatedInPage) {
    return <p>There was an error trying to log you out: 'The logout was not initiated from within the page.'</p>;
  }

  return <p>Processing logout...</p>;
}
